import { ApiError } from "@/lib/api/api-error";
import { AgentGatewayService } from "@/services/agent-gateway-service";
import type { AgentSession } from "@/services/types/agent-gateway";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { agentGatewayQueryKeys } from "./query-options";

type EndSessionMutationVariables = {
  agentKey: string;
  sessionId: string;
  idempotencyKey: string;
};

function getEndSessionError(error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return "You no longer have permission to end this session.";
    }
    if (error.status === 404) {
      return "This session no longer exists.";
    }
    if (error.status === 409) {
      return "The session is being updated. Refresh it and try again.";
    }
    if ([502, 503, 504].includes(error.status)) {
      return "The session could not be ended because the agent service is unavailable. Please try again.";
    }
  }
  return "The session could not be ended. Check your connection and try again.";
}

export function useAgentEndSessionController(
  agentKey: string,
  sessionId: string,
) {
  const queryClient = useQueryClient();
  const scopeKey = `${agentKey}:${sessionId}`;
  const activeScopeRef = useRef(scopeKey);
  const inFlightScopeRef = useRef<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingScope, setPendingScope] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (variables: EndSessionMutationVariables) =>
      AgentGatewayService.endSession(
        variables.agentKey,
        variables.sessionId,
        variables.idempotencyKey,
      ),
    onSuccess: (session, variables) => {
      const sentScope = `${variables.agentKey}:${variables.sessionId}`;
      if (inFlightScopeRef.current === sentScope) {
        inFlightScopeRef.current = null;
      }
      queryClient.setQueryData<AgentSession>(
        agentGatewayQueryKeys.session(variables.agentKey, variables.sessionId),
        session,
      );
      if (activeScopeRef.current === sentScope) {
        setDialogOpen(false);
        setErrorMessage(null);
        setPendingScope(null);
      }
      void queryClient.invalidateQueries({
        exact: true,
        queryKey: agentGatewayQueryKeys.sessions(variables.agentKey),
      });
    },
    onError: (error, variables) => {
      const sentScope = `${variables.agentKey}:${variables.sessionId}`;
      if (inFlightScopeRef.current === sentScope) {
        inFlightScopeRef.current = null;
      }
      if (activeScopeRef.current === sentScope) {
        setErrorMessage(getEndSessionError(error));
        setPendingScope(null);
      }
      if (error instanceof ApiError && [401, 403, 404].includes(error.status)) {
        void queryClient.invalidateQueries({
          queryKey: agentGatewayQueryKeys.availableAgents(),
        });
      }
    },
  });

  useEffect(() => {
    activeScopeRef.current = scopeKey;
    inFlightScopeRef.current = null;
    setDialogOpen(false);
    setErrorMessage(null);
    setPendingScope(null);
  }, [scopeKey]);

  const endSession = () => {
    if (inFlightScopeRef.current) {
      return;
    }
    inFlightScopeRef.current = scopeKey;
    setPendingScope(scopeKey);
    setErrorMessage(null);
    mutation.mutate({
      agentKey,
      sessionId,
      idempotencyKey: crypto.randomUUID(),
    });
  };

  return {
    dialogOpen,
    endSession,
    errorMessage,
    isPending: pendingScope === scopeKey,
    setDialogOpen,
  };
}
